import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getDataCollections } from '../services/api';
import { useUserSession } from './UserSessionContext';

const CACHE_TTL_MS = 60_000;

const CollectionsDataContext = createContext(null);

const withKeys = (rows) =>
  (Array.isArray(rows) ? rows : []).map((c) => ({ ...c, key: c.id.toString() }));

export function CollectionsDataProvider({ children }) {
  const { keycloak } = useKeycloak();
  const { isAdmin, rolesLoaded } = useUserSession();

  const [userCollections, setUserCollections] = useState([]);
  const [platformCollections, setPlatformCollections] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchedAt = useRef(0);
  const userCollectionsRef = useRef(userCollections);
  const platformCollectionsRef = useRef(platformCollections);
  userCollectionsRef.current = userCollections;
  platformCollectionsRef.current = platformCollections;
  const fetchInFlight = useRef(null);

  const isFresh = () => Date.now() - fetchedAt.current < CACHE_TTL_MS;

  const snapshot = () => ({
    user: userCollectionsRef.current,
    platform: platformCollectionsRef.current,
  });

  const ensureCollections = useCallback(
    async (force = false) => {
      if (!keycloak?.authenticated || !rolesLoaded) {
        return snapshot();
      }
      if (!force && fetchedAt.current > 0 && isFresh()) {
        return snapshot();
      }
      if (fetchInFlight.current) return fetchInFlight.current;

      setLoading(true);
      const promise = (async () => {
        try {
          const userPromise = getDataCollections(keycloak, false);
          const platformPromise = isAdmin
            ? getDataCollections(keycloak, true)
            : Promise.resolve([]);
          const [userData, platformData] = await Promise.all([userPromise, platformPromise]);
          const user = withKeys(userData);
          const platform = isAdmin ? withKeys(platformData) : [];
          setUserCollections(user);
          setPlatformCollections(platform);
          fetchedAt.current = Date.now();
          return { user, platform };
        } catch (e) {
          console.error('Failed to load collections:', e);
          return snapshot();
        } finally {
          setLoading(false);
          fetchInFlight.current = null;
        }
      })();
      fetchInFlight.current = promise;
      return promise;
    },
    [keycloak, rolesLoaded, isAdmin]
  );

  const invalidateCollections = useCallback(() => {
    fetchedAt.current = 0;
    fetchInFlight.current = null;
  }, []);

  useEffect(() => {
    if (rolesLoaded && keycloak?.authenticated) {
      ensureCollections();
    }
  }, [rolesLoaded, keycloak?.authenticated, ensureCollections]);

  const value = useMemo(
    () => ({
      userCollections,
      platformCollections,
      collectionsLoading: loading,
      setUserCollections,
      setPlatformCollections,
      ensureCollections,
      invalidateCollections,
    }),
    [
      userCollections,
      platformCollections,
      loading,
      ensureCollections,
      invalidateCollections,
    ]
  );

  return (
    <CollectionsDataContext.Provider value={value}>
      {children}
    </CollectionsDataContext.Provider>
  );
}

export function useCollectionsData() {
  const ctx = useContext(CollectionsDataContext);
  if (!ctx) {
    throw new Error('useCollectionsData must be used within CollectionsDataProvider');
  }
  return ctx;
}
